import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import type { KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils/logging';

import { zodDeliveryNoteInternalSchema } from '../validations';

import { type DeliveryNoteInternal } from './types';
import { zodDeliveryNoteInternalEndpointSchema } from './validations';

/**
 * Fetches internal delivery note data for the provided asset ID.
 *
 * @param assetId - The asset ID for the delivery note to fetch.
 * @param options - The options for the request (includes abort signal).
 * @returns The internal delivery note data if successful.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/get_delivery_note_internal_delivery_note__asset_id__internal_get
 */
export async function fetchDeliveryNoteInternal(
  assetId: UUID,
  options?: KyRequestOptions,
): Promise<DeliveryNoteInternal> {
  const response = await vestigasApi
    .get(ENDPOINT.DELIVERY_NOTE.GET_INTERNAL(assetId), {
      validate: {
        pathSegments: zodDeliveryNoteInternalEndpointSchema,
      },
      ...options,
    })
    .json<DeliveryNoteInternal>();

  // Validate the returned data and log any validation errors
  const validatedResponse = zodDeliveryNoteInternalSchema.safeParse(response);

  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'Delivery note internal API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
}
